import { Component, OnInit } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, take } from 'rxjs';
import { saveAs } from 'file-saver';

import { ZtHistoryTableItem } from 'src/app/components/zt-history-table/zt-history-table.model';

import { ExecutionService } from 'src/app/core/services/execution.service';
import { ZtLoaderService } from 'src/app/shared/components/molecules/zt-loader/zt-loader.service';
import { ExecutionHistoryMapper } from './history.mapper';
import {
    ZtButtonConfig,
    ZtButtonType,
} from 'src/app/shared/components/atoms/zt-button/zt-button.model';
import { MessageService } from 'primeng/api';

@UntilDestroy()
@Component({
    selector: 'history',
    templateUrl: 'history.component.html',
    styleUrls: ['history.component.scss'],
})
export class HistoryComponent implements OnInit {
    history: ZtHistoryTableItem[] = [];
    actionsMap: Map<string, Function> = new Map([
        ['sample', this.playAudio.bind(this)],
        ['download', this.saveAudio.bind(this)],
        ['delete', this.showDeleteExecutionDialog.bind(this)],
    ]);
    shouldShowDeleteExecutionDialog: boolean = false;
    currentExecution: ZtHistoryTableItem | null = null;

    // delete confrim dialog buttons config
    cancelBtnConfig: ZtButtonConfig = {
        id: 'cancel-delete-btn',
        label: 'Cancel',
    };
    confirmDeleteBtnConfig: ZtButtonConfig = {
        id: 'confirm-delete-btn',
        label: 'Delete',
    };

    constructor(
        private loadingService: ZtLoaderService,
        private executionService: ExecutionService,
        private mapper: ExecutionHistoryMapper,
        private messageService: MessageService
    ) {}

    ngOnInit() {
        this.loadingService.showLoader();
        this.executionService
            .getHistory()
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (history: any) => {
                    this.history = this.mapper.map(history, this.actionsMap);
                    this.loadingService.hideLoader();
                },
                error: (error: Error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Could not get the execution history.',
                    });
                    this.loadingService.hideLoader();
                },
            });
    }

    showDeleteExecutionDialog(execution: ZtHistoryTableItem): void {
        this.currentExecution = execution;
        this.shouldShowDeleteExecutionDialog = true;
    }

    closeConfirmDeleteDialog(): void {
        this.currentExecution = null;
        this.shouldShowDeleteExecutionDialog = false;
    }

    confirmDeleteDialog(): void {
        this.shouldShowDeleteExecutionDialog = false;
        this.deleteExecution(this.currentExecution!);
    }

    get ZtButtonType(): typeof ZtButtonType {
        return ZtButtonType;
    }

    private playAudio(execution: ZtHistoryTableItem): void {
        this.loadingService.showLoader();
        this.downloadAudio(execution).subscribe({
            next: (blob: Blob) => {
                this.loadingService.hideLoader();
                execution.sample.showControls = true;
                execution.sample.blob = blob;
                execution.sample.playAudio = true;
            },
            error: (error: Error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Could not download the requested file.',
                });
                this.loadingService.hideLoader();
            },
        });
    }

    private saveAudio(execution: ZtHistoryTableItem): void {
        this.loadingService.showLoader();
        if (execution.sample.blob) {
            saveAs(execution.sample.blob, 'tts-sample');
            this.loadingService.hideLoader();
        } else {
            this.downloadAudio(execution).subscribe({
                next: (blob: Blob) => {
                    execution.sample.blob = blob;
                    execution.sample.playAudio = false;
                    saveAs(blob, 'tts-sample.wav');
                    this.loadingService.hideLoader();
                },
                error: (error: Error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Could not download the requested file.',
                    });
                    this.loadingService.hideLoader();
                },
            });
        }
    }

    private deleteExecution(execution: ZtHistoryTableItem): void {
        this.loadingService.showLoader();
        this.executionService
            .deleteExecution(execution.id)
            .pipe(take(1))
            .subscribe({
                next: (data: any) => {
                    this.history = this.history.filter(
                        (item) => item.id != execution.id
                    );
                    this.loadingService.hideLoader();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Deleted the execution successfully.',
                    });
                },
                error: (error: Error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary:
                            'Could not delete the selected execution from history.',
                    });
                    this.loadingService.hideLoader();
                },
            });
    }

    private downloadAudio(execution: ZtHistoryTableItem): Observable<Blob> {
        return this.executionService
            .getExecutionSample(execution.sample.filePath)
            .pipe(take(1));
    }
}
